import React, { useState, useMemo } from 'react';
import { Radar, Video, ExternalLink, Plane } from 'lucide-react';

// ----------------------- Aircraft type → tail-cam demo -----------------------
// Best-effort YouTube IDs of nose/tail cam footage for common aircraft.
// Used only as a "what this aircraft type looks like in-flight" preview —
// it is NOT a live feed from the actual flight (those are not public).
const AIRCRAFT_TAILCAMS = {
  // Airbus
  A388: { youtubeId: 'lRyhqxg9_3M', title: 'A380 cockpit landing view' },
  A380: { youtubeId: 'lRyhqxg9_3M', title: 'A380 cockpit landing view' },
  A350: { youtubeId: 'eDjuwhSAJTo', title: 'A350 cockpit view' },
  A359: { youtubeId: 'eDjuwhSAJTo', title: 'A350 cockpit view' },
  A35K: { youtubeId: 'eDjuwhSAJTo', title: 'A350-1000 cockpit view' },
  A330: { youtubeId: 'tvjZxA6Xn5g', title: 'A330 cockpit view' },
  A332: { youtubeId: 'tvjZxA6Xn5g', title: 'A330 cockpit view' },
  A333: { youtubeId: 'tvjZxA6Xn5g', title: 'A330 cockpit view' },
  A320: { youtubeId: '8C9wQDqkHnA', title: 'A320 cockpit landing' },
  A321: { youtubeId: '8C9wQDqkHnA', title: 'A320 cockpit landing' },
  // Boeing
  B777: { youtubeId: 'AeAi-iICe-w', title: 'Boeing 777 cockpit landing' },
  B772: { youtubeId: 'AeAi-iICe-w', title: 'Boeing 777-200 cockpit landing' },
  B773: { youtubeId: 'AeAi-iICe-w', title: 'Boeing 777-300 cockpit landing' },
  B77W: { youtubeId: 'AeAi-iICe-w', title: 'Boeing 777-300ER cockpit landing' },
  B787: { youtubeId: 'l5d4DiWeb84', title: 'Boeing 787 Dreamliner cockpit' },
  B788: { youtubeId: 'l5d4DiWeb84', title: 'Boeing 787-8 cockpit' },
  B789: { youtubeId: 'l5d4DiWeb84', title: 'Boeing 787-9 cockpit' },
  B78X: { youtubeId: 'l5d4DiWeb84', title: 'Boeing 787-10 cockpit' },
  B744: { youtubeId: '1MMpyTUEooo', title: 'Boeing 747 cockpit takeoff' },
  B748: { youtubeId: '1MMpyTUEooo', title: 'Boeing 747-8 cockpit takeoff' },
  B737: { youtubeId: 'BU-K9YpHlz4', title: 'Boeing 737 cockpit POV' },
  B738: { youtubeId: 'BU-K9YpHlz4', title: 'Boeing 737-800 cockpit POV' },
  B739: { youtubeId: 'BU-K9YpHlz4', title: 'Boeing 737-900 cockpit POV' },
  // Generic fallback
  DEFAULT: { youtubeId: '8C9wQDqkHnA', title: 'Commercial aircraft cockpit POV' },
};

const pickTailCam = (aircraftIata) => {
  if (!aircraftIata) return AIRCRAFT_TAILCAMS.DEFAULT;
  const code = aircraftIata.toUpperCase();
  if (AIRCRAFT_TAILCAMS[code]) return AIRCRAFT_TAILCAMS[code];
  // Try prefix match (e.g. B77W → B777)
  const prefix3 = code.slice(0, 3);
  if (AIRCRAFT_TAILCAMS[prefix3]) return AIRCRAFT_TAILCAMS[prefix3];
  return AIRCRAFT_TAILCAMS.DEFAULT;
};

// ----------------------- Live ATC radar embed --------------------------------
// Uses airplanes.live (free, ad-free, fully iframable mirror of ADS-B Exchange).
// We centre the iframe on the aircraft's current position (or departure airport
// if the flight isn't yet airborne).
const LiveRadarEmbed = ({ lat, lng, depLat, depLng }) => {
  const centerLat = Number.isFinite(lat) ? lat : depLat;
  const centerLng = Number.isFinite(lng) ? lng : depLng;
  if (!Number.isFinite(centerLat) || !Number.isFinite(centerLng)) {
    return (
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-6 text-center text-sm text-slate-500">
        Live radar unavailable — no position known yet.
      </div>
    );
  }
  // airplanes.live globe — iframable
  const src = `https://globe.airplanes.live/?lat=${centerLat.toFixed(3)}&lon=${centerLng.toFixed(3)}&zoom=6&hideButtons&hideSidebar`;
  const fr24 = `https://www.flightradar24.com/${centerLat.toFixed(2)},${centerLng.toFixed(2)}/6`;
  return (
    <div className="relative rounded-xl overflow-hidden border border-slate-800 bg-slate-900" data-testid="live-radar-embed">
      <iframe
        title="Live air-traffic radar"
        src={src}
        className="w-full h-[360px] sm:h-[420px] border-0"
        loading="lazy"
      />
      <a
        href={fr24}
        target="_blank"
        rel="noopener noreferrer"
        className="absolute top-2 right-2 bg-black/70 hover:bg-black text-white text-[11px] font-semibold rounded-lg px-2.5 py-1 inline-flex items-center gap-1 backdrop-blur"
        data-testid="open-flightradar24"
      >
        Open in Flightradar24 <ExternalLink className="h-3 w-3" />
      </a>
    </div>
  );
};

// ----------------------- Tail-cam YouTube embed ------------------------------
const TailCamEmbed = ({ aircraftIata, airlineName }) => {
  const cam = useMemo(() => pickTailCam(aircraftIata), [aircraftIata]);
  const src = `https://www.youtube-nocookie.com/embed/${cam.youtubeId}?rel=0&modestbranding=1`;
  return (
    <div className="bg-slate-900 rounded-xl overflow-hidden border border-slate-800" data-testid="tail-cam-embed">
      <div className="aspect-video">
        <iframe
          title={cam.title}
          src={src}
          className="w-full h-full"
          allow="accelerometer; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
      <div className="px-3 py-2 text-[11px] text-slate-400 flex items-center justify-between">
        <span>
          <Plane className="inline-block h-3 w-3 mr-1" />
          {airlineName ? `${airlineName} · ` : ''}
          {aircraftIata ? `${aircraftIata} demo: ${cam.title}` : cam.title}
        </span>
        <span className="text-[10px] opacity-60">Demo · not live from this aircraft</span>
      </div>
    </div>
  );
};

// ----------------------- Main component --------------------------------------
export const LiveCameras = ({ lat, lng, depLat, depLng, aircraftIata, airlineName }) => {
  const [tab, setTab] = useState('radar');
  return (
    <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden" data-testid="live-cameras">
      <div className="flex border-b border-slate-200 bg-slate-50">
        <button
          type="button"
          onClick={() => setTab('radar')}
          data-testid="cameras-tab-radar"
          className={`flex-1 px-4 py-3 text-sm font-semibold inline-flex items-center justify-center gap-2 transition ${
            tab === 'radar'
              ? 'text-emerald-700 border-b-2 border-emerald-600 bg-white'
              : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          <Radar className="h-4 w-4" /> Live Radar
        </button>
        <button
          type="button"
          onClick={() => setTab('tailcam')}
          data-testid="cameras-tab-tailcam"
          className={`flex-1 px-4 py-3 text-sm font-semibold inline-flex items-center justify-center gap-2 transition ${
            tab === 'tailcam'
              ? 'text-emerald-700 border-b-2 border-emerald-600 bg-white'
              : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          <Video className="h-4 w-4" /> Tail-cam Demo
        </button>
      </div>
      <div className="p-3">
        {tab === 'radar' ? (
          <>
            <div className="text-[11px] text-slate-500 mb-2">
              Real-time air-traffic radar showing every transponder-equipped aircraft around your flight,
              powered by airplanes.live (ADS-B feed).
            </div>
            <LiveRadarEmbed lat={lat} lng={lng} depLat={depLat} depLng={depLng} />
          </>
        ) : (
          <>
            <div className="text-[11px] text-slate-500 mb-2">
              Preview of a tail/nose-camera view from the same aircraft type that's flying this route.
              Cockpit footage is not broadcast live from the actual flight.
            </div>
            <TailCamEmbed aircraftIata={aircraftIata} airlineName={airlineName} />
          </>
        )}
      </div>
    </div>
  );
};

export default LiveCameras;
